import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { MailCheck, Loader2, RefreshCw, Zap, Shield, Inbox, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
    if (user?.email_verified) navigate('/');
  }, [token, user, navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('New verification link sent to your email!');
        setCooldown(60);
      } else {
        toast.error(data.detail || 'Failed to resend');
      }
    } catch {
      toast.error('Failed to resend verification link');
    } finally {
      setResending(false);
    }
  };

  const maskedEmail = user?.email
    ? user.email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + b.replace(/./g, '*') + c)
    : '';

  return (
    <div className="min-h-screen flex" data-testid="verify-email-page">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-5/12 relative bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600/20 via-slate-900 to-cyan-600/20" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
              <Zap className="w-5 h-5" />
            </div>
            <span className="text-xl font-semibold tracking-tight">Inspiro Tracker</span>
          </div>

          <div className="max-w-md space-y-8">
            <div>
              <h1 className="text-3xl font-semibold mb-3 leading-tight tracking-tight">
                Almost there!<br />
                <span className="bg-gradient-to-r from-sky-400 to-cyan-400 bg-clip-text text-transparent">Check your email.</span>
              </h1>
              <p className="text-slate-400 leading-relaxed">
                We've sent a verification link to your email. Click the link to activate your account and start your free trial.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Inbox className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">Check your inbox</p>
                  <p className="text-xs text-slate-500">Look for an email from Inspiro Tracker with a verification link</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">Also check spam/junk</p>
                  <p className="text-xs text-slate-500">Sometimes verification emails end up in your spam folder</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm text-slate-600">Secure email verification</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-7/12 flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8 justify-center">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Inspiro Tracker</span>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-none p-8 md:p-10">
            {/* Icon */}
            <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-cyan-100 dark:from-sky-500/20 dark:to-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MailCheck className="w-8 h-8 text-sky-600 dark:text-sky-400" />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 text-center tracking-tight" data-testid="verify-title">
              Check your email
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 text-center leading-relaxed">
              We've sent a verification link to
            </p>
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-6 text-center" data-testid="verify-email-display">
              {maskedEmail || user?.email}
            </p>

            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 rounded-xl p-4 mb-6" data-testid="verify-instructions">
              <div className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Click the link in your email</p>
                  <p className="text-xs text-blue-600 mt-1">
                    Open the email and click <strong>"Verify My Email"</strong> to activate your account and start your free trial.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 rounded-xl p-3 mb-6">
              <p className="text-xs text-amber-700 text-center">
                The link expires in 24 hours. Check your spam/junk folder if you don't see it.
              </p>
            </div>

            {/* Resend section */}
            <div className="text-center space-y-3 pt-2">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Didn't receive the email?
              </p>
              <Button
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                variant="outline"
                className="h-10 px-5 rounded-xl text-sm font-medium"
                data-testid="resend-btn"
              >
                {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend verification link'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
